import { usePersistedState } from "../lib/usePersistedState";
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import EntityForm from "../components/EntityForm";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ENTITIES } from "../config/entities";
import { useEntity, createItem } from "../hooks/useEntity";
import { apiError } from "../lib/api";

export default function GenericModule({ entityKey, subtitle }) {
  const cfg = ENTITIES[entityKey];
  const statusKey = cfg.statusKey || "status";
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = usePersistedState(`filters:${entityKey}:status`, "all");
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const params = { search };
  if (statusFilter !== "all") params[statusKey] = statusFilter;
  const { items, total, loading, reload } = useEntity(cfg.resource, params);

  // New records default their status to the first option (e.g. "Active" for contacts).
  const createInitial = useMemo(() => ({ [statusKey]: cfg.statusOptions?.[0] }), [cfg, statusKey]);

  const submit = async (form) => {
    setSaving(true);
    try {
      const payload = cfg.resource === "contacts" ? { product_name: "Arq+", ...form } : form;
      await createItem(cfg.resource, payload);
      toast.success(`${cfg.singular} created`);
      setFormOpen(false);
      reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto">
      <PageHeader
        title={cfg.title}
        subtitle={subtitle}
        count={total}
        actions={<Button onClick={() => setFormOpen(true)} data-testid="new-record-btn"><Plus className="h-4 w-4 mr-1.5" />New {cfg.singular}</Button>}
      />

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder={`Search ${cfg.title.toLowerCase()}…`} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {cfg.statusOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <DataTable columns={cfg.columns} items={items} loading={loading} onRowClick={(r) => navigate(`/record/${cfg.resource}/${r.id}`)} emptyLabel={`No ${cfg.title.toLowerCase()} found`} />

      <EntityForm
        open={formOpen}
        onOpenChange={setFormOpen}
        fields={cfg.fields}
        initial={createInitial}
        onSubmit={submit}
        saving={saving}
        title={`New ${cfg.singular}`}
      />
    </div>
  );
}
